import React, { createContext, useContext, useState, useEffect } from 'react';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { CssBaseline } from '@mui/material';

const ThemeContext = createContext();

// Light theme configuration
const lightTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#1976d2',
      light: '#42a5f5',
      dark: '#1565c0',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#dc004e',
      light: '#ff5983',
      dark: '#9a0036',
      contrastText: '#ffffff',
    },
    accent: {
      main: '#ff9800',
      light: '#ffb74d',
      dark: '#f57c00',
    },
    background: {
      default: '#f8fafc',
      paper: '#ffffff',
    },
    text: {
      primary: '#1e293b',
      secondary: '#64748b',
    },
    success: {
      main: '#10b981',
      light: '#34d399',
      dark: '#059669',
    },
    warning: {
      main: '#f59e0b',
      light: '#fbbf24',
      dark: '#d97706',
    },
    error: {
      main: '#ef4444',
      light: '#f87171',
      dark: '#dc2626',
    },
    info: {
      main: '#3b82f6',
      light: '#60a5fa',
      dark: '#2563eb',
    },
    divider: '#e2e8f0',
  },
  typography: {
    fontFamily: '"Poppins", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: { fontSize: '2.5rem', fontWeight: 700, lineHeight: 1.2 },
    h2: { fontSize: '2rem', fontWeight: 600, lineHeight: 1.3 },
    h3: { fontSize: '1.75rem', fontWeight: 600, lineHeight: 1.3 },
    h4: { fontSize: '1.5rem', fontWeight: 600, lineHeight: 1.4 },
    h5: { fontSize: '1.25rem', fontWeight: 600, lineHeight: 1.4 },
    h6: { fontSize: '1.125rem', fontWeight: 600, lineHeight: 1.4 },
    body1: { fontSize: '12px', lineHeight: 1.5 },
    body2: { fontSize: '12px', lineHeight: 1.5 },
    button: { fontSize: '12px', fontWeight: 600 },
    caption: { fontSize: '12px' },
    overline: { fontSize: '12px' },
    subtitle1: { fontSize: '12px' },
    subtitle2: { fontSize: '12px' },
  },
  shape: { borderRadius: 12 },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: '0px 4px 6px -1px rgba(0, 0, 0, 0.1)',
          border: '1px solid #e2e8f0',
          borderRadius: 16,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          borderRadius: 10,
          padding: '10px 24px',
          fontSize: '12px',
          fontFamily: '"Poppins", sans-serif',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': { 
            borderRadius: 10,
            fontSize: '12px',
            fontFamily: '"Poppins", sans-serif',
          },
          '& .MuiInputLabel-root': {
            fontSize: '12px',
            fontFamily: '"Poppins", sans-serif',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: { borderRadius: 16 },
      },
    },
    MuiTypography: {
      styleOverrides: {
        root: {
          fontFamily: '"Poppins", sans-serif',
        },
        h1: {
          fontSize: '2.5rem',
          fontFamily: '"Poppins", sans-serif',
        },
        h2: {
          fontSize: '2rem',
          fontFamily: '"Poppins", sans-serif',
        },
        h3: {
          fontSize: '1.75rem',
          fontFamily: '"Poppins", sans-serif',
        },
        h4: {
          fontSize: '1.5rem',
          fontFamily: '"Poppins", sans-serif',
        },
        h5: {
          fontSize: '1.25rem',
          fontFamily: '"Poppins", sans-serif',
        },
        h6: {
          fontSize: '1.125rem',
          fontFamily: '"Poppins", sans-serif',
        },
        body1: {
          fontSize: '12px',
          fontFamily: '"Poppins", sans-serif',
        },
        body2: {
          fontSize: '12px',
          fontFamily: '"Poppins", sans-serif',
        },
        subtitle1: {
          fontSize: '12px',
          fontFamily: '"Poppins", sans-serif',
        },
        subtitle2: {
          fontSize: '12px',
          fontFamily: '"Poppins", sans-serif',
        },
        caption: {
          fontSize: '12px',
          fontFamily: '"Poppins", sans-serif',
        },
        overline: {
          fontSize: '12px',
          fontFamily: '"Poppins", sans-serif',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontSize: '12px',
          fontFamily: '"Poppins", sans-serif',
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          fontSize: '12px',
          fontFamily: '"Poppins", sans-serif',
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontSize: '12px',
          fontFamily: '"Poppins", sans-serif',
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          fontSize: '12px',
          fontFamily: '"Poppins", sans-serif',
        },
      },
    },
    MuiListItemText: {
      styleOverrides: {
        primary: {
          fontSize: '12px',
          fontFamily: '"Poppins", sans-serif',
        },
        secondary: {
          fontSize: '12px',
          fontFamily: '"Poppins", sans-serif',
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          fontSize: '12px',
          fontFamily: '"Poppins", sans-serif',
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontSize: '12px',
          fontFamily: '"Poppins", sans-serif',
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          fontSize: '12px',
        },
      },
    },
    MuiSvgIcon: {
      styleOverrides: {
        root: {
          fontSize: '12px',
        },
      },
    },
  },
});

// Dark theme configuration
const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#3b82f6',
      light: '#60a5fa',
      dark: '#2563eb',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#f43f5e',
      light: '#fb7185',
      dark: '#e11d48',
      contrastText: '#ffffff',
    },
    accent: {
      main: '#f59e0b',
      light: '#fbbf24',
      dark: '#d97706',
    },
    background: {
      default: '#0f172a',
      paper: '#1e293b',
    },
    text: {
      primary: '#f1f5f9',
      secondary: '#94a3b8',
    },
    success: {
      main: '#22c55e',
      light: '#4ade80',
      dark: '#16a34a',
    },
    warning: {
      main: '#f59e0b',
      light: '#fbbf24',
      dark: '#d97706',
    },
    error: {
      main: '#ef4444',
      light: '#f87171',
      dark: '#dc2626',
    },
    info: {
      main: '#3b82f6',
      light: '#60a5fa',
      dark: '#2563eb',
    },
    divider: '#334155',
  },
  typography: {
    fontFamily: '"Poppins", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: { fontSize: '2.5rem', fontWeight: 700, lineHeight: 1.2 },
    h2: { fontSize: '2rem', fontWeight: 600, lineHeight: 1.3 },
    h3: { fontSize: '1.75rem', fontWeight: 600, lineHeight: 1.3 },
    h4: { fontSize: '1.5rem', fontWeight: 600, lineHeight: 1.4 },
    h5: { fontSize: '1.25rem', fontWeight: 600, lineHeight: 1.4 },
    h6: { fontSize: '1.125rem', fontWeight: 600, lineHeight: 1.4 },
    body1: { fontSize: '12px', lineHeight: 1.5 },
    body2: { fontSize: '12px', lineHeight: 1.5 },
    button: { fontSize: '12px', fontWeight: 600 },
    caption: { fontSize: '12px' },
    overline: { fontSize: '12px' },
    subtitle1: { fontSize: '12px' },
    subtitle2: { fontSize: '12px' },
  },
  shape: { borderRadius: 12 },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: '0px 4px 6px -1px rgba(0, 0, 0, 0.3)',
          border: '1px solid #334155',
          borderRadius: 16,
          backgroundImage: 'none',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          borderRadius: 10,
          padding: '10px 24px',
          fontSize: '12px',
          fontFamily: '"Poppins", sans-serif',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': { 
            borderRadius: 10,
            '& fieldset': { borderColor: '#475569' },
            '&:hover fieldset': { borderColor: '#64748b' },
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: { 
          borderRadius: 16,
          backgroundImage: 'none',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundImage: 'none',
          borderRight: '1px solid #334155',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        root: {
          fontFamily: '"Poppins", sans-serif',
        },
        h1: {
          fontSize: '2.5rem',
          fontFamily: '"Poppins", sans-serif',
        },
        h2: {
          fontSize: '2rem',
          fontFamily: '"Poppins", sans-serif',
        },
        h3: {
          fontSize: '1.75rem',
          fontFamily: '"Poppins", sans-serif',
        },
        h4: {
          fontSize: '1.5rem',
          fontFamily: '"Poppins", sans-serif',
        },
        h5: {
          fontSize: '1.25rem',
          fontFamily: '"Poppins", sans-serif',
        },
        h6: {
          fontSize: '1.125rem',
          fontFamily: '"Poppins", sans-serif',
        },
        body1: {
          fontSize: '12px',
          fontFamily: '"Poppins", sans-serif',
        },
        body2: {
          fontSize: '12px',
          fontFamily: '"Poppins", sans-serif',
        },
        subtitle1: {
          fontSize: '12px',
          fontFamily: '"Poppins", sans-serif',
        },
        subtitle2: {
          fontSize: '12px',
          fontFamily: '"Poppins", sans-serif',
        },
        caption: {
          fontSize: '12px',
          fontFamily: '"Poppins", sans-serif',
        },
        overline: {
          fontSize: '12px',
          fontFamily: '"Poppins", sans-serif',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontSize: '12px',
          fontFamily: '"Poppins", sans-serif',
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          fontSize: '12px',
          fontFamily: '"Poppins", sans-serif',
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontSize: '12px',
          fontFamily: '"Poppins", sans-serif',
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          fontSize: '12px',
          fontFamily: '"Poppins", sans-serif',
        },
      },
    },
    MuiListItemText: {
      styleOverrides: {
        primary: {
          fontSize: '12px',
          fontFamily: '"Poppins", sans-serif',
        },
        secondary: {
          fontSize: '12px',
          fontFamily: '"Poppins", sans-serif',
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          fontSize: '12px',
          fontFamily: '"Poppins", sans-serif',
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontSize: '12px',
          fontFamily: '"Poppins", sans-serif',
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          fontSize: '12px',
        },
      },
    },
    MuiSvgIcon: {
      styleOverrides: {
        root: {
          fontSize: '12px',
        },
      },
    },
  },
});

export const CustomThemeProvider = ({ children }) => {
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const savedTheme = localStorage.getItem('restaurant-admin-theme');
    return savedTheme === 'dark';
  });

  const toggleTheme = () => {
    const newTheme = !isDarkMode;
    setIsDarkMode(newTheme);
    localStorage.setItem('restaurant-admin-theme', newTheme ? 'dark' : 'light');
  };

  const theme = isDarkMode ? darkTheme : lightTheme;

  const value = {
    isDarkMode,
    toggleTheme,
    theme,
  };

  return (
    <ThemeContext.Provider value={value}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ThemeContext.Provider>
  );
};

export const useCustomTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useCustomTheme must be used within a CustomThemeProvider');
  }
  return context;
};
